/**
 * Страница возврата после оплаты — /pay/return?id=<наш id>
 *
 * ЮKassa возвращает человека сюда сразу, а уведомление об оплате может прийти чуть позже.
 * Поэтому страница спрашивает статус у нашего сервера несколько раз, а не решает сама.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuthHeaders } from '../lib/auth-headers';

type Status = { status: string; project: string; itemId: string; amount: number };

export function PayReturnPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const id = params.get('id') || '';
  const [st, setSt] = useState<Status | null>(null);
  const [tries, setTries] = useState(0);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!id) { setErr('Нет номера платежа'); return; }
    let stop = false;
    const tick = async () => {
      try {
        const r = await fetch(`/api/pay/${id}/status`, { headers: getAuthHeaders() });
        const d = await r.json();
        if (!r.ok) { setErr(d.error || 'Ошибка'); return; }
        if (stop) return;
        setSt(d);
        if (d.status === 'pending' && tries < 12) setTimeout(() => setTries((t) => t + 1), 2500);
      } catch { setErr('Нет связи с сервером, попробуйте обновить страницу'); }
    };
    tick();
    return () => { stop = true; };
  }, [id, tries]);

  const courseLink = st?.project === 'courses' ? `/collections/${st.itemId}` : '/';

  return (
    <div className="min-h-screen bg-[#F7F7F4] text-[#252A2D] p-4">
      <div className="max-w-md mx-auto mt-16 bg-white border border-[#E1E5E3] rounded-2xl p-6 border border-[#E1E5E3] text-center space-y-4">
        {err && <p className="text-red-300">{err}</p>}
        {!err && (!st || st.status === 'pending') && (
          <>
            <div className="text-4xl">⏳</div>
            <h1 className="text-xl font-bold">Проверяем оплату</h1>
            <p className="text-[#626B73] text-sm">Обычно это несколько секунд. Страницу можно не обновлять.</p>
            {tries >= 12 && (
              <p className="text-[#0F766E] text-sm">Подтверждение задерживается. Если деньги списались, доступ откроется автоматически, когда банк подтвердит платёж. Напишите в поддержку, если через 10 минут его нет.</p>
            )}
          </>
        )}
        {st?.status === 'succeeded' && (
          <>
            <div className="text-4xl">✅</div>
            <h1 className="text-xl font-bold">Оплата прошла</h1>
            <p className="text-[#626B73] text-sm">{st.amount} ₽ получены, доступ открыт.</p>
            <button onClick={() => navigate(courseLink)} className="w-full py-3 bg-white border border-[#E1E5E3] hover:bg-white/30 rounded-xl font-medium">
              Перейти к курсу
            </button>
          </>
        )}
        {st?.status === 'canceled' && (
          <>
            <div className="text-4xl">✖️</div>
            <h1 className="text-xl font-bold">Оплата не прошла</h1>
            <p className="text-[#626B73] text-sm">Деньги не списаны. Можно попробовать ещё раз.</p>
            <button onClick={() => navigate(courseLink)} className="w-full py-3 bg-white border border-[#E1E5E3] hover:bg-white/30 rounded-xl font-medium">
              Вернуться к курсу
            </button>
          </>
        )}
        <a href="/support" className="block text-xs text-purple-400 hover:text-[#0F766E]">Поддержка</a>
      </div>
    </div>
  );
}
